import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Flex } from 'antd';
import styled from 'styled-components';
import { Modal } from '../../../common/components/modal/Modal';
import { ModalFooter } from '../../../common/components/modal/ModalFooter';
import { ModalHeader } from '../../../common/components/modal/ModalHeader';
import { ModalTitle } from '../../../common/components/modal/ModalTitle';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { AlgorithmConfidentialityRuleFormField } from './confidentialityRule/AlgorithmConfidentialityRuleFormField';
import translations from './AlgorithmSourcesFormModal.translations.json';
import {
  AlgorithmSourceFormDataType,
  AlgorithmSourcesValidFormDataType,
} from './schema/types';
import {
  Form,
  CreateFormTableRowButton,
  DeleteFormTableRowButton,
  FormField,
  FormInput,
  FormSelect,
  getFormTableFieldName,
  TanstackFormTable,
  confirmPanelFormClose,
  FormAlerts,
  useAlertContext,
} from '../../../common/form';
import {
  SystemClassifierOption,
  useSystemClassifierSelectOptions,
} from '../../../configuration';
import {
  DataObjectFormDataType,
  DataObjectOutputType,
  DataObjectType,
} from '../../types';
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import { TFunction } from 'i18next';
import {
  colorGray,
  colorTableOperationIcon,
} from '@atlas-frontend/design-tokens';
import * as Icon from 'react-feather';
import { Padding } from '../../../common/components/Padding';
import { AlgorithmSourcePeriod } from './AlgorithmSourcePeriod';
import { AlgorithmSourceOutSourceSelect } from './AlgorithmSourceOutSourceSelect';
import { AlgorithmSourceInSourceSelect } from './AlgorithmSourceInSourceSelect';
import { confirmationHeaders } from '../../../common/utils';
import { GetApiErrorMessage } from '../../../common/utils/ValidationMessagesMap';
import { useMutationErrors } from '../../../common/queryClient';
import { AlgorithmSourcesSchema } from './schema/AlgorithmSourcesSchema';
import { useMutateAlgorithmSourcesQuery } from './query/useMutateAlgorithmSourcesQuery';
import { ActionButton } from '../../../common/components';
import { useFormState } from 'react-hook-form';
import validationTranslations from '../../translations/validation.translations.json';
import { DataObjectDescriptionVersionStore } from '../../DataObjectDescriptionVersionStore';
import { useGetAlgorithmSourcesQuery } from './query/useGetAlgorithmSourcesQuery';

const { addTranslations, t, useTranslation } = i18nNsFactory(
  'algorithm-sources-modal',
);
addTranslations(translations);
addTranslations(validationTranslations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export const defaultSourceFormData: AlgorithmSourceFormDataType = {
  id: -1,
  sourceName: '',
  sourceCode: '',
  sourceType: '',
  sourceId: '' as any,
  domainId: null as any,
  dataObjectCode: null,
  numberOfPreviousPeriod: 0,
  alias: '',
  dimensions: [],
  indicators: [],
  dataObjectDescription: {} as any,
  dataObjectType: null,
  outputTypeCode: null,
};

export interface AlgorithmSourcesFormModalProps {
  algorithmId: number;
  onClose?: (changed: boolean) => void;
  onLoadingStateChange?: (isLoading: boolean) => void;
}

export const AlgorithmSourcesFormModal: FC<AlgorithmSourcesFormModalProps> = (
  props,
) => {
  const { algorithmId, onClose, onLoadingStateChange } = props;
  const {
    data: algorithmSources,
    refetch,
    isFetching,
  } = useGetAlgorithmSourcesQuery({
    id: algorithmId,
  });

  useEffect(() => {
    onLoadingStateChange?.(isFetching);
  }, [isFetching, onLoadingStateChange]);

  const { mutateAsync } = useMutateAlgorithmSourcesQuery();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();

  const onSuccess = useCallback(() => {
    void refetch();
    onClose?.(true);
    setSuccess();
  }, [refetch, onClose, setSuccess]);

  const handleSubmit = useCallback(
    (values: AlgorithmSourcesValidFormDataType, confirm: boolean) => {
      resetAlert();
      clearMutationErrors();
      return mutateAsync({
        id: algorithmId,
        data: values,
        headers: confirmationHeaders(confirm),
      })
        .then(onSuccess)
        .catch((error) => {
          setMutationErrors(error?.response?.data?.errors ?? []);
        });
    },
    [
      resetAlert,
      clearMutationErrors,
      mutateAsync,
      algorithmId,
      onSuccess,
      setMutationErrors,
    ],
  );

  const defaultValues = useMemo(
    () => ({
      sources: algorithmSources?.sources?.length
        ? algorithmSources?.sources
        : [defaultSourceFormData],
      confidentialityRule: algorithmSources?.confidentialityRule || {
        useDimension: false,
      },
    }),
    [algorithmSources],
  );

  if (isFetching) {
    return null;
  }

  return (
    <Form
      schema={AlgorithmSourcesSchema.validFormDataSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      serverErrors={mutationErrors}
      idAttributePrefix="algorithmSources"
    >
      <AlgorithmSourcesFormModalInner onClose={() => onClose?.(false)} />
    </Form>
  );
};

const columnHelper = createColumnHelper<AlgorithmSourceFormDataType>();

function getColumns(
  t: TFunction,
  defaultDataObjectValues: DataObjectFormDataType,
  domainAttributes?: SystemClassifierOption[],
): ColumnDef<AlgorithmSourceFormDataType, any>[] {
  {
    const columns: ColumnDef<AlgorithmSourceFormDataType, any>[] = [];
    if (defaultDataObjectValues.dataObjectType === DataObjectType.OUT) {
      columns.push(
        columnHelper.accessor('domainId', {
          enableColumnFilter: false,
          enableSorting: false,
          cell: (props) => renderDomainSelect(props, domainAttributes),
          meta: {
            width: 60,
            className: 'hide-box-shadow',
          },
        }),
        columnHelper.accessor('sourceType', {
          enableColumnFilter: false,
          enableSorting: false,
          cell: (props) => renderOutTypeSelect(props, defaultDataObjectValues),
          meta: {
            width: 60,
            className: 'hide-box-shadow',
          },
        }),
      );
    }

    columns.push(
      columnHelper.accessor('sourceId', {
        enableColumnFilter: false,
        enableSorting: false,
        cell: (props) => renderSourceSelect(props, defaultDataObjectValues),
        meta: {
          width: 150,
          className: 'hide-box-shadow',
        },
      }),
      columnHelper.accessor('numberOfPreviousPeriod', {
        enableColumnFilter: false,
        enableSorting: false,
        cell: (props) => {
          const name = getFormTableFieldName(props);
          return (
            <AlgorithmSourcePeriod name={name} rowIndex={props.row.index} />
          );
        },
        meta: {
          width: 50,
          className: 'hide-box-shadow',
        },
      }),
      columnHelper.accessor('alias', {
        enableColumnFilter: false,
        enableSorting: false,
        cell: (props) => {
          const name = getFormTableFieldName(props);
          return (
            <FormField name={name} label={t('alias.label')}>
              <FormInput name={name} />
            </FormField>
          );
        },
        meta: {
          width: 80,
          className: 'hide-box-shadow',
        },
      }),
      columnHelper.accessor('operations' as any, {
        enableSorting: false,
        enableColumnFilter: false,
        cell: (props) => {
          const rows = props.table.getCenterRows().length;
          return (
            <FlexWrapper vertical justify="end">
              <Flex justify="left" gap="small">
                {props.row.index + 1 === rows && (
                  <CreateFormTableRowButtonWrapper
                    defaultRowValues={defaultSourceFormData}
                    icon={
                      <Icon.Plus size={12} color={colorTableOperationIcon} />
                    }
                    autoFocusField="domainId"
                  />
                )}
                <DeleteFormTableRowButtonWrapper
                  context={props}
                  icon={
                    <Icon.Minus size={12} color={colorTableOperationIcon} />
                  }
                />
              </Flex>
            </FlexWrapper>
          );
        },
        meta: {
          width: 50,
          className: 'sticky-right hide-box-shadow',
        },
      }),
    );

    return columns;
  }
}

function AlgorithmSourcesFormModalInner({ onClose }: { onClose?: () => void }) {
  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');
  const { DOMAIN } = useSystemClassifierSelectOptions(['DOMAIN']);
  const { cols } = useSourceTable(dataObjectDescriptionVersion, DOMAIN);
  const { isDirty } = useFormState();
  const handleCancel = useCallback(() => {
    if (isDirty) {
      confirmPanelFormClose(onClose);
    } else {
      onClose?.();
    }
  }, [isDirty, onClose]);

  return (
    <Modal
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <ActionButton type="abort" onClick={handleCancel} />
            <ActionButton type="formSubmit" />
          </Flex>
        </ModalFooter>
      }
      onCancel={handleCancel}
      width="70rem"
    >
      <ModalHeader>
        <ModalTitle>{t(`title`)}</ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <Flex vertical gap="large">
          <StyledFormTable name={`sources`} columns={cols} hideHeader />
          {dataObjectDescriptionVersion.outputTypeCode ===
            DataObjectOutputType.REPORT && (
            <AlgorithmConfidentialityRuleFormField
              dataObjectConfidentialityStatusId={
                dataObjectDescriptionVersion.confidentialityStatusId
              }
            />
          )}
        </Flex>
      </Padding>
    </Modal>
  );
}

function useSourceTable(
  defaultDataObjectValues: DataObjectFormDataType,
  domainAttributes?: SystemClassifierOption[],
) {
  const { t } = useTranslation();

  const cols = useMemo(() => {
    return getColumns(t, defaultDataObjectValues, domainAttributes);
  }, [t, defaultDataObjectValues, domainAttributes]);

  return { cols };
}

const renderDomainSelect = (
  props: CellContext<AlgorithmSourceFormDataType, any>,
  domainAttributes?: SystemClassifierOption[],
) => {
  const name = getFormTableFieldName(props);
  return (
    <FormField name={name} label={t('domain.label')}>
      <FormSelect name={name} options={domainAttributes} allowClear />
    </FormField>
  );
};

const renderOutTypeSelect = (
  props: CellContext<AlgorithmSourceFormDataType, any>,
  defaultDataObjectValues: DataObjectFormDataType,
) => {
  const name = getFormTableFieldName(props);
  const outputTypeCode = defaultDataObjectValues.outputTypeCode;

  const typeOptions = [
    {
      label: t('type.IN'),
      value: 'IN',
    },
    {
      label: t('type.OUTPUT'),
      value: 'OUTPUT',
    },
    {
      label: t('type.INTERMEDIATE'),
      value: 'INTERMEDIATE',
    },
    {
      label: t('type.REPORT'),
      value: 'REPORT',
      disabled: outputTypeCode !== DataObjectOutputType.REPORT,
    },
  ];

  return (
    <FormField name={name} label={t('type.label')}>
      <FormSelect name={name} options={typeOptions} allowClear />
    </FormField>
  );
};

const renderSourceSelect = (
  props: CellContext<AlgorithmSourceFormDataType, any>,
  defaultDataObjectValues: DataObjectFormDataType,
) => {
  const name = getFormTableFieldName(props);
  const index = props.row.index;

  return (
    <FormField name={name} label={t('source.label')} required>
      {defaultDataObjectValues.dataObjectType === DataObjectType.IN ? (
        <AlgorithmSourceInSourceSelect
          name={name}
          index={index}
          defaultDataObjectValues={defaultDataObjectValues}
        />
      ) : (
        <AlgorithmSourceOutSourceSelect
          name={name}
          index={index}
          defaultDataObjectValues={defaultDataObjectValues}
        />
      )}
    </FormField>
  );
};

const DeleteFormTableRowButtonWrapper = styled(DeleteFormTableRowButton)`
  border-color: ${colorGray[3]};
`;

const CreateFormTableRowButtonWrapper = styled(CreateFormTableRowButton)`
  border-color: ${colorGray[3]};
`;

const FlexWrapper = styled(Flex)`
  height: 100%;
`;

const StyledFormTable = styled(TanstackFormTable<any, any, any>)`
  td {
    padding: 0.7rem 0.5rem;
    border: none;
  }
  tr:nth-child(n + 2) .form-field > label {
    display: none;
  }
`;
